// File: src/pages/Friends.js

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { io } from "socket.io-client";
import { useSearchParams, useNavigate } from "react-router-dom";


// Mobile-friendly components
import MatchesAndRequestsPage from "../components/friends/MatchesAndRequestsPage";
import ChatPage from "../components/friends/ChatPage";
import ContactPage from "../components/friends/ContactPage";

export default function Friends() {
  // ------------------------------
  // 1) State
  // ------------------------------
  const [activeTab, setActiveTab] = useState("matches");

  const [requests, setRequests] = useState([]);
  const [matches, setMatches] = useState([]);

  const [chatRooms, setChatRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");

  const [searchTerm, setSearchTerm] = useState("");

  const currentUserId = localStorage.getItem("userId");
  const [socket, setSocket] = useState(null);
  const prevRoomRef = useRef(null);

  // For the notification CTA => reading "?roomId=..." and also focusRequestId
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const paramRoomId = searchParams.get("roomId") || null;
  const focusRequestId = searchParams.get("focusRequestId") || null;

  // Two-way sync: update activeTab from "?tab="
  useEffect(() => {
    const newTab = searchParams.get("tab");
    if (newTab && newTab !== activeTab) {
      console.log("[Friends.js] Updating activeTab from URL param:", newTab);
      setActiveTab(newTab);
    }
  }, [searchParams, activeTab]);

  // Update local state & URL query param.
  function handleTabChange(newTab) {
    console.log("[Friends.js] Changing tab to:", newTab);
    setActiveTab(newTab);
    const newParams = new URLSearchParams(window.location.search);
    newParams.set("tab", newTab);
    newParams.delete("focusRequestId");
    navigate({ pathname: window.location.pathname, search: newParams.toString() });
  }

  // ------------------------------
  // 2) getOtherUserLocal
  // ------------------------------
  function getOtherUserLocal(room) {
    if (!room) return null;
    const userA = room.userAId;
    const userB = room.userBId;
    if (!userA || !userB) return null;

    const userAId = userA._id ? userA._id.toString() : String(userA);
    const userBId = userB._id ? userB._id.toString() : String(userB);
    console.log(
      "[Friends.js] getOtherUserLocal: currentUserId =",
      currentUserId,
      "userAId =",
      userAId,
      "userBId =",
      userBId
    );
    return userAId === currentUserId ? userB : userA;
  }


  // Returns the "other" side of a ChatRequest relative to me
function getOtherFromRequest(req, me) {
  const from = req?.fromUserId?._id ?? req?.fromUserId;
  const to   = req?.toUserId?._id   ?? req?.toUserId;
  const meStr = String(me);
  const fromStr = String(from || '');
  const toStr   = String(to   || '');
  const otherId = (fromStr === meStr) ? toStr : fromStr;

  // Prefer populated object if it exists on the request
  let otherObj = (fromStr === meStr) ? req?.toUserId : req?.fromUserId;
  if (otherObj && typeof otherObj === 'object') return { otherId, otherObj };
  return { otherId, otherObj: { _id: otherId } };
}


  // ------------------------------
  // 3) Socket.IO Setup
  // ------------------------------
  useEffect(() => {
    if (!currentUserId) {
      console.log("[Friends.js] No userId; skipping socket connection.");
      return;
    }

    // CHANGE #1: Retrieve the access token (instead of jwt)
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.log("[Friends.js] No access token found; skipping socket connection.");
      return;
    }

    console.log("[Friends.js] Initializing Socket.IO for userId =", currentUserId);
    const newSocket = io(process.env.REACT_APP_API_URL, {
      auth: { token: accessToken },
      withCredentials: true,
      transports: ['websocket', 'polling'],   // try WS first, fall back to polling
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 300,
      reconnectionDelayMax: 3000,
      timeout: 8000,                          // fail fast so the manager retries quickly
    });

    newSocket.emit("joinUserRoom", currentUserId);
    setSocket(newSocket);

    newSocket.on("connect_error", (err) => {
      console.log("[Friends.js] Socket connect error =>", err);
    });
    newSocket.on("disconnect", (reason) => {
      console.log("[Friends.js] Socket disconnected =>", reason);
    });
    newSocket.on("reconnect", (attemptNumber) => {
      console.log(`[Friends.js] Socket reconnected (attempt #${attemptNumber})`);
      newSocket.emit("joinUserRoom", currentUserId);
      if (selectedRoom && selectedRoom._id) {
        newSocket.emit("joinChatRoom", {
          roomId: selectedRoom._id,
          userId: currentUserId,
        });
      }
    });

    // A) chatRequestUpdated => re-fetch requests
    newSocket.on("chatRequestUpdated", async (updatedRequest) => {
      console.log("[Friends.js] chatRequestUpdated received:", updatedRequest);
      try {
        const resp = await axios.get(
          `${process.env.REACT_APP_API_URL}/chat/requests?userId=${currentUserId}`
        );
        console.log("[Friends.js] Updated requests received:", resp.data);
        setRequests(resp.data);
      } catch (err) {
        console.error("[Friends.js] Error refreshing requests:", err);
      }
    });

    // B) chatRequestDeleted => re-fetch requests
    newSocket.on("chatRequestDeleted", async (data) => {
      console.log("[Friends.js] chatRequestDeleted received:", data);
      try {
        const resp = await axios.get(
          `${process.env.REACT_APP_API_URL}/chat/requests?userId=${currentUserId}`
        );
        console.log("[Friends.js] Updated requests received after deletion:", resp.data);
        setRequests(resp.data);
      } catch (err) {
        console.error("[Friends.js] Error fetching requests after deletion:", err);
      }
    });

    // C) chatRoomUpdated => merge into top-level chatRooms
    newSocket.on("chatRoomUpdated", (updatedRoom) => {
      console.log("[Friends.js] chatRoomUpdated received:", updatedRoom);
      setChatRooms((prev) => {
        const idx = prev.findIndex((r) => r._id === updatedRoom._id);
        if (idx === -1) return [...prev, updatedRoom];
        const newRooms = [...prev];
        newRooms[idx] = updatedRoom;
        return newRooms;
      });
    });

    // D) newMessage => update lastMessage in chatRooms & deduplicate
    newSocket.on("newMessage", (raw) => {
      // normalize: guarantee `_id` and `content`
      const msg = {
        ...raw,
        _id: raw._id || raw.messageId || raw.id || `m-${Date.now()}`,
        content: typeof raw.content === "string" ? raw.content : (raw.text ?? ""),
      };
    
      setMessages((prev) => {
        // If we previously pushed an optimistic message, replace it by (sender,content,optimistic) OR by temp id
        const optimisticIdx = prev.findIndex(
          (m) => m.optimistic && m.content === msg.content && m.senderId === msg.senderId
        );
        if (optimisticIdx !== -1) {
          const next = [...prev];
          next[optimisticIdx] = { ...msg, optimistic: false };
          return next;
        }
    
        // Deduplicate by _id
        if (prev.some((m) => m._id === msg._id)) return prev;
    
        return [...prev, msg];
      });
    });
    
    return () => {
      console.log("[Friends.js] Cleaning up socket on unmount");
      newSocket.disconnect();
    };
  }, [currentUserId, selectedRoom]);

  // Hide navbar if a chatroom is open (Mobile-like behavior)
  useEffect(() => {
    const navEl = document.getElementById("mainNavbar");
    if (navEl) {
      navEl.style.display = selectedRoom ? "none" : "";
    }
  }, [selectedRoom]);

  // ------------------------------
  // 4) "beforeunload" => leave chatRoom on refresh
  // ------------------------------
  useEffect(() => {
    if (!socket || !selectedRoom) return;
    const handleBeforeUnload = () => {
      console.log("[Friends.js] beforeunload: leaving chatRoom", selectedRoom._id);
      socket.emit("leaveChatRoom", {
        roomId: selectedRoom._id,
        userId: currentUserId,
      });
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [socket, selectedRoom, currentUserId]);
// ------------------------------
// 5) Fetch requests & matches if tab = "matches"
// ------------------------------
useEffect(() => {
  if (!currentUserId) return;
  if (activeTab === "matches") {
    console.log("[Friends.js] currentUserId =", currentUserId);
    console.log("[Friends.js] Fetching requests + matches list + legacy mixers");

    // Requests (unchanged)
    axios
      .get(`${process.env.REACT_APP_API_URL}/chat/requests?userId=${currentUserId}`)
      .then((res) => {
        console.log("[Friends.js] /chat/requests →", res.data?.length ?? 0, "items");
        setRequests(res.data);
      })
      .catch((err) => console.error("[Friends.js] Error fetching requests:", err));

    // NEW: fetch both sources in parallel
    const urlList   = `${process.env.REACT_APP_API_URL}/matches/list?userId=${currentUserId}`;
    const urlLegacy = `${process.env.REACT_APP_API_URL}/matches?userId=${currentUserId}`;

    Promise.allSettled([axios.get(urlList), axios.get(urlLegacy)])
      .then(([listRes, legacyRes]) => {
        const listArr   = (listRes.status === "fulfilled"   && Array.isArray(listRes.value.data)) ? listRes.value.data   : [];
        const legacyArr = (legacyRes.status === "fulfilled" && Array.isArray(legacyRes.value.data)) ? legacyRes.value.data : [];

        // Keep all mixer rows from legacy (includes pending/accepted/in-session/completed)
        const legacyMixerOnly = legacyArr.filter(m => m && m.isMixer === true);

        // Merge: prefer /matches/list for non-mixer (it already has your reveal/speedmatch logic),
        // and union in all mixer rows from legacy.
        const merged = [...listArr, ...legacyMixerOnly];

        // Deduplicate by match _id if present, else by (participants unordered + eventId)
        const seen = new Set();
        const out = [];
        for (const m of merged) {
          const id = m?._id ? `id:${m._id}` : (() => {
            const p = (m?.participants || []).map(x => String(x?._id || x)).sort().join("_");
            const e = String(m?.eventId?._id || m?.eventId || "");
            return `pe:${p}|${e}`;
          })();
          if (!seen.has(id)) {
            seen.add(id);
            out.push(m);
          }
        }

        console.log(
          "[Friends.js] merged matches →",
          out.length,
          "items; mixers in result =",
          out.filter(x => x.isMixer === true).length
        );
        setMatches(out);
      })
      .catch((err) => {
        console.error("[Friends.js] Error fetching matches:", err);
        setMatches([]); // fail-safe
      });
  }
}, [activeTab, currentUserId]);



  // ------------------------------
  // 6) Fetch chatRooms if tab = "chats"
  // ------------------------------
  useEffect(() => {
    if (!currentUserId) return;
    if (activeTab === "chats") {
      axios
        .get(`${process.env.REACT_APP_API_URL}/chat/rooms-with-last-msg?userId=${currentUserId}`)
        .then((res) => {
          console.log("[Friends.js] Fetched chatRooms:", res.data);
          setChatRooms(res.data);
        })
        .catch((err) => console.error("[Friends.js] Error fetching chat rooms:", err));
    }
  }, [activeTab, currentUserId]);

  // ------------------------------
  // 7) Fetch messages when selectedRoom changes
  // ------------------------------
  useEffect(() => {
    if (activeTab !== "chats" || !selectedRoom) {
      setMessages([]);
      return;
    }
    console.log("[Friends.js] Fetching messages for roomId =", selectedRoom._id);
    axios
      .get(`${process.env.REACT_APP_API_URL}/chat/rooms/${selectedRoom._id}/messages`)
      .then((res) => {
        console.log("[Friends.js] Fetched messages:", res.data);
        setMessages(res.data);
      })
      .catch((err) => console.error("[Friends.js] Error fetching messages:", err));
  }, [activeTab, selectedRoom]);

  // ------------------------------
  // 8) Leave old room, mark read, join new room
  // ------------------------------
  useEffect(() => {
    if (!socket) return;
    const oldRoom = prevRoomRef.current;
    if (oldRoom && oldRoom._id) {
      console.log("[Friends.js] Leaving old room:", oldRoom._id);
      socket.emit("leaveChatRoom", {
        roomId: oldRoom._id,
        userId: currentUserId,
      });
      axios
        .patch(`${process.env.REACT_APP_API_URL}/chat/rooms/${oldRoom._id}/mark-read`, {
          userId: currentUserId,
        })
        .then(() =>
          axios.get(`${process.env.REACT_APP_API_URL}/chat/rooms-with-last-msg?userId=${currentUserId}`)
        )
        .then((chatRes) => {
          console.log("[Friends.js] Updated chatRooms after marking read:", chatRes.data);
          setChatRooms(chatRes.data);
        })
        .catch((err) => console.error("[Friends.js] Error marking old room read:", err));
    }
    if (selectedRoom && selectedRoom._id) {
      console.log("[Friends.js] Joining new room:", selectedRoom._id);
      socket.emit("joinChatRoom", {
        roomId: selectedRoom._id,
        userId: currentUserId,
      });
    }
    prevRoomRef.current = selectedRoom;
  }, [selectedRoom, socket, currentUserId]);

  // ------------------------------
  // 9) If user picks "matches" => clear selectedRoom
  // ------------------------------
  useEffect(() => {
    if (activeTab !== "chats" && selectedRoom) {
      console.log("[Friends.js] User switched away from chats; clearing selectedRoom.");
      setSelectedRoom(null);
      setMessages([]);
    }
  }, [activeTab, selectedRoom]);

  // ------------------------------
  // 10) Chat handlers
  // ------------------------------
  function handleOpenRoom(room) {
    console.log("[Friends.js] handleOpenRoom =>", room._id);
    setSelectedRoom(room);
    const newParams = new URLSearchParams(window.location.search);
    newParams.set("tab", "chats");
    newParams.set("roomId", room._id);
    navigate({ pathname: window.location.pathname, search: newParams.toString() });
  }

  async function handleSendMessage() {
    if (!selectedRoom || !newMsg.trim()) return;
  
    const tempMessageId = `temp-${Date.now()}`;
  
    const optimisticMessage = {
      _id: tempMessageId,
      senderId: currentUserId,
      content: newMsg,
      createdAt: new Date().toISOString(),
      optimistic: true,  // keep this flag to identify optimistic messages clearly
    };
  
    setMessages((prev) => [...prev, optimisticMessage]);
    setNewMsg("");
  
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/chat/rooms/${selectedRoom._id}/messages`, {
        senderId: currentUserId,
        content: newMsg,
      });
  
      const confirmedMessage = response.data;
  
      setMessages((prev) => prev.map((msg) =>
        msg._id === tempMessageId ? confirmedMessage : msg
      ));
  
      console.log("[Friends.js] Message confirmed by server.");
    } catch (err) {
      console.error("[Friends.js] Error sending message:", err);
      setMessages((prev) => prev.filter((msg) => msg._id !== tempMessageId));
    }
  }
  
  
  

  function handleBackToChats() {
    console.log("[Friends.js] handleBackToChats: clearing selectedRoom");
    setSelectedRoom(null);
    const newParams = new URLSearchParams(window.location.search);
    newParams.delete("roomId");
    newParams.set("tab", "chats");
    navigate({ pathname: window.location.pathname, search: newParams.toString() });
  }

  // ------------------------------
  // 11) Request Flow (UPDATED interpretRequest)
  // ------------------------------
  function interpretRequest(req) {
    if (!req) return null;

    const fromId = req.fromUserId._id
      ? req.fromUserId._id.toString()
      : req.fromUserId.toString();
    const toId = req.toUserId._id
      ? req.toUserId._id.toString()
      : req.toUserId.toString();

    // We define if I'm the sender or the receiver
    const iAmSender = fromId === currentUserId;
    const iAmReceiver = toId === currentUserId;

    // Evaluate the request's status
    if (req.status === "PENDING") {
      // If I'm the sender => "SENT"
      if (iAmSender) return { category: "SENT" };
      // If I'm the receiver => "NEED_DECIDE"
      if (iAmReceiver) return { category: "NEED_DECIDE" };
    } else if (req.status === "ACCEPTED") {
      return { category: "ACCEPTED" };
    } else if (req.status === "DECLINED") {
      // If I declined it => "DECLINED_BY_ME", else => "SENT"
      if (req.declinerId && req.declinerId.toString() === currentUserId) {
        return { category: "DECLINED_BY_ME" };
      }
      // If someone else declined => it's effectively "SENT" from me?
      return { category: "SENT" };
    }

    // Otherwise, default fallback
    return null;
  }

  function findPairRequest(currentId, otherId, eventIdStr, allReqs) {
    return (
      allReqs.find((r) => {
        if (r.status === "CANCELLED") return false;
        const fromId = r.fromUserId._id
          ? r.fromUserId._id.toString()
          : r.fromUserId.toString();
        const toId = r.toUserId._id
          ? r.toUserId._id.toString()
          : r.toUserId.toString();
        return (
          (fromId === currentId && toId === otherId) ||
          (fromId === otherId && toId === currentId)
        );
      }) || null
    );
  }

  // We'll keep the accepted => needDecide => potential approach
  const acceptedArr = [];
  const needDecideArr = [];
  const potentialArr = [];

  matches.forEach((match) => {
    const eventIdStr = String(match?.eventId?._id ?? match?.eventId ?? "");
    if (!eventIdStr) return;

    const participants = match.participants || [];
    const others = participants.filter(
      (p) => String(p._id ?? p) !== String(currentUserId)
    );

    // === Contacts fallback: include ACCEPTED ChatRequests even if no match row exists ===
const acceptedFromRequests = [];
(requests || []).forEach((req) => {
  if (!req || req.status !== 'ACCEPTED') return;

  const { otherId, otherObj } = getOtherFromRequest(req, currentUserId);

  // Skip if already present from matches path
  const already = acceptedArr.some(x => String(x.otherUser?._id || '') === otherId);
  if (already) return;

  // Build a minimal row compatible with MatchesAndRequestsPage + ContactPage
  acceptedFromRequests.push({
    match: null,                      // no match row; purely a contact-level link
    req,                              // the ACCEPTED ChatRequest
    view: { category: 'ACCEPTED' },   // same shape interpretRequest() would produce
    otherUser: otherObj,              // populated object if available on req
  });
});

// Merge into accepted; your existing dedupe below will keep first per user
acceptedArr.push(...acceptedFromRequests);


    others.forEach((otherUser) => {
      const otherUserId = String(otherUser._id ?? otherUser);
      const req = findPairRequest(currentUserId, otherUserId, eventIdStr, requests);
      const view = interpretRequest(req);

      if (!req && match.status === "pending") {
        potentialArr.push({ match, req: null, view: null, otherUser });
      } else if (!req) {
        potentialArr.push({ match, req: null, view: null, otherUser });
      } else if (view?.category === "ACCEPTED") {
        acceptedArr.push({ match, req, view, otherUser });
      } else if (view?.category === "NEED_DECIDE" || view?.category === "DECLINED_BY_ME") {
        needDecideArr.push({ match, req, view, otherUser });
      } else if (view?.category === "SENT") {
        potentialArr.push({ match, req, view, otherUser });
      } else {
        potentialArr.push({ match, req, view, otherUser });
      }
    });
  });

  console.log("[Friends.js] acceptedArr =>", acceptedArr);
  console.log("[Friends.js] needDecideArr =>", needDecideArr);
  console.log("[Friends.js] potentialArr =>", potentialArr);

  // Deduplicate: "Accepted => Need => Potential"
  const usedUserIds = new Set();

  function filterUnique(arr, label) {
    const result = [];
    arr.forEach((item) => {
      const userId = String(item.otherUser?._id || "");
      if (!userId) return;
      if (usedUserIds.has(userId)) {
        console.log(`[${label}] skipping => userId=${userId}`);
        return;
      }
      console.log(`[${label}] keeping => userId=${userId}`);
      usedUserIds.add(userId);
      result.push(item);
    });
    return result;
  }

  const dedupedAccepted = filterUnique(acceptedArr, "ACCEPTED");
  const dedupedNeedDecide = filterUnique(needDecideArr, "NEED_DECIDE");
  const dedupedPotential = filterUnique(potentialArr, "POTENTIAL");

  // ------------------------------
  // 12) The request + chat handlers remain same
  // ------------------------------
  async function handleSendRequest(matchId, otherUserId) {
    console.log("[Friends.js] handleSendRequest =>", { matchId, otherUserId });
  
    const tempRequestId = `temp-${Date.now()}`;
    const matchDoc = matches.find((m) => m._id === matchId);
    const eventIdToSend = matchDoc?.eventId?._id ?? matchDoc?.eventId;
  
    const optimisticRequest = {
      _id: tempRequestId,
      fromUserId: currentUserId,
      toUserId: otherUserId,
      eventId: eventIdToSend,
      status: "PENDING",
      optimistic: true, // Critical flag
    };
  
    // Add the optimistic request
    setRequests((prevRequests) => [...prevRequests, optimisticRequest]);
  
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/chat/requests`, {
        fromUserId: currentUserId,
        toUserId: otherUserId,
        eventId: eventIdToSend,
      });
  
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/chat/requests?userId=${currentUserId}`);
  
      // **Key Step**: Explicitly remove the optimistic request before updating
      setRequests(res.data);
      
      console.log("[Friends.js] Request confirmed by server.");
    } catch (err) {
      console.error("[Friends.js] handleSendRequest => error =>", err);
  
      // Remove the optimistic request if the request fails
      setRequests((prevRequests) => prevRequests.filter((r) => r._id !== tempRequestId));
    }
  }
  

  async function handleCancelRequest(requestId) {
    console.log("[Friends.js] handleCancelRequest =>", requestId);
  
    // Optimistically remove the request
    const prevRequests = [...requests];
    setRequests((prev) => prev.filter((r) => r._id !== requestId));
  
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/chat/requests/${requestId}`, {
        action: "CANCEL",
        userId: currentUserId,
      });
  
      const resp = await axios.get(`${process.env.REACT_APP_API_URL}/chat/requests?userId=${currentUserId}`);
      setRequests(resp.data);
  
      console.log("[Friends.js] Request cancellation confirmed.");
    } catch (err) {
      console.error("[Friends.js] Error in handleCancelRequest =>", err);
      // Revert if error
      setRequests(prevRequests);
    }
  }
  

  async function handleAcceptRequest(requestId) {
    console.log("[Friends.js] handleAcceptRequest =>", requestId);
  
    // Optimistically mark as accepted
    const prevRequests = [...requests];
    setRequests((prev) =>
      prev.map((r) =>
        r._id === requestId ? { ...r, status: "ACCEPTED", optimistic: true } : r
      )
    );
  
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/chat/requests/${requestId}`, {
        status: "ACCEPTED",
        userId: currentUserId,
      });
  
      const resp = await axios.get(`${process.env.REACT_APP_API_URL}/chat/requests?userId=${currentUserId}`);
      setRequests(resp.data);
  
      console.log("[Friends.js] Request acceptance confirmed.");
    } catch (err) {
      console.error("[Friends.js] Error in handleAcceptRequest =>", err);
      // Revert if error
      setRequests(prevRequests);
    }
  }
  

  async function handleDeclineRequest(requestId) {
    console.log("[Friends.js] handleDeclineRequest =>", requestId);
  
    // Optimistically mark as declined
    const prevRequests = [...requests];
    setRequests((prev) =>
      prev.map((r) =>
        r._id === requestId ? { ...r, status: "DECLINED", declinerId: currentUserId, optimistic: true } : r
      )
    );
  
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/chat/requests/${requestId}`, {
        status: "DECLINED",
        declinerId: currentUserId,
        userId: currentUserId,
      });
  
      const resp = await axios.get(`${process.env.REACT_APP_API_URL}/chat/requests?userId=${currentUserId}`);
      setRequests(resp.data);
  
      console.log("[Friends.js] Request decline confirmed.");
    } catch (err) {
      console.error("[Friends.js] Error in handleDeclineRequest =>", err);
      // Revert if error
      setRequests(prevRequests);
    }
  }
  

  async function handleUndoDeclineRequest(requestId) {
    console.log("[Friends.js] handleUndoDeclineRequest =>", requestId);
  
    // ✅ Save current state to revert if necessary
    const prevRequests = [...requests];
  
    // ✅ Immediately optimistically update request status to PENDING
    setRequests((prev) =>
      prev.map((r) =>
        r._id === requestId
          ? { ...r, status: "PENDING", declinerId: null, optimistic: true }
          : r
      )
    );
  
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/chat/requests/${requestId}`, {
        status: "PENDING",
        declinerId: null,
        userId: currentUserId,
      });
  
      // ✅ Confirm with latest data from server
      const resp = await axios.get(`${process.env.REACT_APP_API_URL}/chat/requests?userId=${currentUserId}`);
      setRequests(resp.data);
      console.log("[Friends.js] Undo decline confirmed by server.");
    } catch (err) {
      console.error("[Friends.js] Error in handleUndoDeclineRequest =>", err);
      // ✅ Immediately revert UI back to original state if error occurs
      setRequests(prevRequests);
    }
  }
  

  async function handleStartChat(otherUserId) {
    const me = String(currentUserId || '').trim();
    const other = String(otherUserId || '').trim();
    if (!me || !other || me === other) {
      console.warn('[Friends.js] handleStartChat guard failed', { me, other });
      return;
    }
  
    console.log('[Friends.js] handleStartChat =>', { userAId: me, userBId: other });
  
    try {
      // Server expects both userAId and userBId
      const createRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/chat/rooms`,
        { userAId: me, userBId: other }
      );
  
      const newRoom = createRes.data;
      if (!newRoom?._id) {
        console.warn('[Friends.js] handleStartChat => missing room _id in response');
        return;
      }
  
      // Refresh list, open the room
      const roomsRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat/rooms-with-last-msg?userId=${me}`
      );
      setChatRooms(roomsRes.data);
  
      const found = (roomsRes.data || []).find(r => String(r._id) === String(newRoom._id));
      if (!found) {
        console.warn('[Friends.js] Created room not in refreshed list yet');
        return;
      }
  
      setSelectedRoom(found);
      handleTabChange('chats');
  
      const params = new URLSearchParams(window.location.search);
      params.set('tab', 'chats');
      params.set('roomId', found._id);
      navigate({ pathname: window.location.pathname, search: params.toString() });
    } catch (err) {
      const status = err?.response?.status;
      const payload = err?.response?.data;
      console.error('[Friends.js] handleStartChat error =>', status, payload || err.message);
  
      // Fallback: if the server responded 409/400 because the room already exists,
      // try to locate it in the user’s room list and open it.
      try {
        const roomsRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/chat/rooms-with-last-msg?userId=${me}`
        );
        setChatRooms(roomsRes.data);
  
        const existing = (roomsRes.data || []).find(r => {
          const a = String(r.userAId?._id || r.userAId);
          const b = String(r.userBId?._id || r.userBId);
          return (a === me && b === other) || (a === other && b === me);
        });
  
        if (existing) {
          setSelectedRoom(existing);
          handleTabChange('chats');
          const params = new URLSearchParams(window.location.search);
          params.set('tab', 'chats');
          params.set('roomId', existing._id);
          navigate({ pathname: window.location.pathname, search: params.toString() });
        }
      } catch (e2) {
        console.error('[Friends.js] fallback lookup failed =>', e2?.response?.data || e2.message);
      }
    }
  }
  
  // ------------------------------
  // 14) Auto-open ?roomId + fallback fetch
  // ------------------------------
  const hasAutoOpenedRef = useRef(false);

  useEffect(() => {
    if (focusRequestId) return;

    if (!paramRoomId) {
      hasAutoOpenedRef.current = false;
      if (selectedRoom) {
        console.log("[Friends.js] paramRoomId is null; clearing selectedRoom");
        setSelectedRoom(null);
      }
      return;
    }

    if (hasAutoOpenedRef.current) return;

    if (activeTab !== "chats") {
      handleTabChange("chats");
      return;
    }

    if (selectedRoom && selectedRoom._id === paramRoomId) return;

    const found = chatRooms.find((r) => r._id === paramRoomId);
    if (found) {
      console.log("[Friends.js] Auto-opening room:", found._id);
      handleOpenRoom(found);
      hasAutoOpenedRef.current = true;
    } else {
      axios
        .get(`${process.env.REACT_APP_API_URL}/chat/rooms/${paramRoomId}`)
        .then((res) => {
          if (!res.data || !res.data._id) return;
          setChatRooms((prev) => {
            const idx = prev.findIndex((x) => x._id === res.data._id);
            if (idx === -1) return [...prev, res.data];
            const newArr = [...prev];
            newArr[idx] = res.data;
            return newArr;
          });
          console.log("[Friends.js] Fetched room for auto-open:", res.data._id);
          handleOpenRoom(res.data);
          hasAutoOpenedRef.current = true;
        })
        .catch((err) => {
          console.error("[Friends.js] Error fetching single room:", err);
        });
    }
  }, [paramRoomId, focusRequestId, activeTab, chatRooms, selectedRoom]);

  useEffect(() => {
  function handleVisibilityChange() {
    // If the tab is no longer hidden (i.e., user has returned)
    if (!document.hidden) {
      console.log("[Friends.js] Page became visible again, refreshing UI...");

      // 2) Re-fetch data for the selectedRoom (or entire chatRooms if desired)
      if (selectedRoom && activeTab === "chats") {
        axios
          .get(`${process.env.REACT_APP_API_URL}/chat/rooms/${selectedRoom._id}/messages`)
          .then((res) => {
            console.log("[Friends.js] Refetched messages on visibilitychange:", res.data);
            setMessages(res.data);
          })
          .catch((err) => console.error("[Friends.js] Error refetching messages:", err));

        // Optionally re-fetch chatRooms or anything else you want updated
        axios
          .get(`${process.env.REACT_APP_API_URL}/chat/rooms-with-last-msg?userId=${currentUserId}`)
          .then((res) => {
            console.log("[Friends.js] Refetched chatRooms on visibilitychange:", res.data);
            setChatRooms(res.data);
          })
          .catch((err) => console.error("[Friends.js] Error refetching chatRooms:", err));
      }
    }
  }

  // 3) Add the visibilitychange listener
  document.addEventListener("visibilitychange", handleVisibilityChange);

  // Cleanup on unmount
  return () => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
}, [selectedRoom, activeTab, currentUserId]);


  // ------------------------------
  // 15) handleUserBlocked
  // ------------------------------
  function handleUserBlocked(blockedUserId) {
    console.log("[Friends.js] handleUserBlocked =>", blockedUserId);
    setChatRooms((prev) =>
      prev.filter((room) => {
        const a = room.userAId._id || room.userAId;
        const b = room.userBId._id || room.userBId;
        return a.toString() !== blockedUserId && b.toString() !== blockedUserId;
      })
    );
    setMatches((prev) =>
      prev.filter((m) => {
        const participantIds = m.participants.map((p) => p._id || p);
        return !participantIds.includes(blockedUserId);
      })
    );
    setRequests((prev) =>
      prev.filter((req) => {
        const fromId = req.fromUserId._id || req.fromUserId;
        const toId = req.toUserId._id || req.toUserId;
        return fromId.toString() !== blockedUserId && toId.toString() !== blockedUserId;
      })
    );
    if (selectedRoom) {
      const selA = selectedRoom.userAId._id || selectedRoom.userAId;
      const selB = selectedRoom.userBId._id || selectedRoom.userBId;
      if (selA.toString() === blockedUserId || selB.toString() === blockedUserId) {
        setSelectedRoom(null);
        setMessages([]);
      }
    }
    handleTabChange("chats");
  }

 // ------------------------------
// 16) Styles
// ------------------------------
const styles = {
  container: {
    fontFamily:
      "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    color: "#111",
  },

  sectionTitle: {
    margin: "0 0 12px 0",
    fontSize: ".85rem",
    fontWeight: 800,
    color: "#111",
    letterSpacing: "-0.01em",
  },

  divider: {
    border: "none",
    borderTop: "1px solid #E5E7EB",
    margin: "20px 0",
  },

  toggleBar: {
    display: "inline-flex",
    gap: "16px",
    marginBottom: "12px",
  },

  toggleButton: {
    cursor: "pointer",
    fontSize: "0.9rem",
    border: "none",
    background: "transparent",
    padding: "4px 0",
    borderBottom: "0px solid transparent",
    color: "#9CA3AF",
    fontWeight: 600,
    transition: "color 0.2s ease, border-color 0.2s ease",
  },


  searchPlusContainer: {
    paddingLeft: 0,
    paddingRight: 12,
    marginBottom: 0,
  },

  searchPlusRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },

  searchInput: {
    flex: 1,
    fontSize: "0.9rem",
    padding: "6px 12px",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    color: "#111",
    background: "#fff",
  },

  plusBtn: {
    minWidth: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#111",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "1rem",
  },

  subDivider: {
    border: "none",
    borderTop: "1px solid #E5E7EB",
    marginTop: 20,
  },

  chatContainer: {
    display: "flex",
    height: "75vh",
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    overflow: "hidden",
  },

  chatList: {
    width: 340,
    borderRight: "1px solid #E5E7EB",
    overflowY: "auto",
  },

  chatMain: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },

  ghostState: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9CA3AF",
    fontSize: "0.95rem",
    backgroundColor: "#FAFAFA",
  },

  // MOBILE
  mobileToggleBar: {
    display: "flex",
    justifyContent: "space-around",
    borderBottom: "1px solid #E5E7EB",
    marginBottom: 8,
  },

  mobileTab: {
    flex: 1,
    textAlign: "center",
    padding: "10px 0",
    cursor: "pointer",
    color: "#9CA3AF",
    fontWeight: 600,
    fontSize: "0.75rem",
    borderBottom: "2px solid transparent",
    transition: "color 0.2s ease, border-color 0.2s ease",
  },

  mobileTabActive: {
    color: "#111",
    borderBottom: "1px solid #111",
    fontWeight: 900,
  },
};


  // ------------------------------
  // 17) Detect Mobile
  // ------------------------------
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const isMobile = windowWidth < 768;

  // ------------------------------
  // 18) Return MOBILE layout
  // ------------------------------
  if (isMobile) {
    return (
      <div style={styles.container}>
        {!selectedRoom && (
          <div style={styles.mobileToggleBar}>
            <div
              onClick={() => handleTabChange("matches")}
              style={
                activeTab === "matches"
                  ? { ...styles.mobileTab, ...styles.mobileTabActive }
                  : styles.mobileTab
              }
            >
              MATCHES
            </div>
            <div
              onClick={() => handleTabChange("chats")}
              style={
                activeTab === "chats"
                  ? { ...styles.mobileTab, ...styles.mobileTabActive }
                  : styles.mobileTab
              }
            >
              CHATS
            </div>
            <div
              onClick={() => handleTabChange("contacts")}
              style={
                activeTab === "contacts"
                  ? { ...styles.mobileTab, ...styles.mobileTabActive }
                  : styles.mobileTab
              }
            >
              FRIENDS
            </div>
          </div>
        )}

        {activeTab === "matches" && !selectedRoom && (
          <MatchesAndRequestsPage
            // only needDecide + potential for matches
            needDecide={dedupedNeedDecide}
            potential={dedupedPotential}
            // accepted => pass empty => you want them in "Contacts" only
            accepted={dedupedAccepted}
                        currentUserId={currentUserId}
            onCancel={handleCancelRequest}
            onAccept={handleAcceptRequest}
            onDecline={handleDeclineRequest}
            onUndo={handleUndoDeclineRequest}
            onSendRequest={handleSendRequest}
            onUserBlocked={handleUserBlocked}
            onMessage={handleStartChat}
          />
        )}

        {activeTab === "chats" && (
          <ChatPage
            chatRooms={chatRooms}
            selectedRoom={selectedRoom}
            messages={messages}
            newMsg={newMsg}
            onNewMsgChange={setNewMsg}
            onSendMessage={handleSendMessage}
            onOpenRoom={handleOpenRoom}
            onBackToChats={handleBackToChats}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            getOtherUserLocal={getOtherUserLocal}
            currentUserId={currentUserId}
            onUserBlocked={handleUserBlocked}
          />
        )}

        {activeTab === "contacts" && !selectedRoom && (
          <ContactPage
            // pass accepted here => they show up in "Contacts"
            accepted={dedupedAccepted}
            currentUserId={currentUserId}
            onMessage={handleStartChat}
            onUserBlocked={handleUserBlocked}
          />
        )}
      </div>
    );
  }
}
